"use client";

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocale } from "@/i18n";
import { useAuth } from "@/hooks";
import { isSafeAppPath, withLocale } from "@/helpers";
import { APP_ORIGIN } from "@/constants";

interface UseRequireGuestOptions {
  redirectTo?: string;
  autoRedirect?: boolean;
  redirectDelay?: number;
  shouldRedirect?: (user: any) => boolean;
  checkEmailVerification?: boolean;
  unverifiedEmailRedirect?: string;
}

interface UseRequireGuestReturn {
  isGuest: boolean;
  isLoading: boolean;
  user: any;
}

const useRequireGuest = ({
  redirectTo,
}: UseRequireGuestOptions): UseRequireGuestReturn => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const locale = useLocale();
  const isGuest = !isAuthenticated;

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    if (!user.onboardingCompleted && user.role !== "demo") {
      navigate(withLocale(locale, "/verification"));
      return;
    }

    if (redirectTo && isSafeAppPath(redirectTo) && redirectTo !== "/") {
      navigate(redirectTo);
      return;
    }

    const base = APP_ORIGIN || "http://localhost:3001";
    window.location.assign(`${base.replace(/\/+$/, "")}/${locale}`);
  }, [isLoading, isAuthenticated, user, redirectTo, locale, navigate]);

  return {
    isGuest,
    isLoading,
    user,
  };
};

export { useRequireGuest };
